import { useGroups } from '@/react/portainer/environments/environment-groups/queries';

import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { FormSection } from '@@/form-components/FormSection';
import { Option, PortainerSelect } from '@@/form-components/PortainerSelect';
import { TextTip } from '@@/Tip/TextTip';

import { Environment } from '../../environments/types';
import { EnvironmentGroup } from '../../environments/environment-groups/types';
import { useEnvironmentList } from '../../environments/queries';
import {
  isDockerEnvironment,
  isKubernetesEnvironment,
} from '../../environments/utils';
import { PolicyEnvironmentType, PolicyType } from '../types';

export interface PolicyConfigurationValues {
  name: string;
  type: PolicyType;
  environmentGroupIds: number[];
}

interface Props {
  onChange: (values: PolicyConfigurationValues) => void;
  environmentType: PolicyEnvironmentType;
  policyConfiguration: PolicyConfigurationValues;
  disableCategory?: boolean;
}

/**
 * Policy Configuration Form Section
 *
 * A non-Formik form section for configuring basic policy properties:
 * - Policy name
 * - Policy category (security, setup, rbac)
 * - Environment groups (their id)
 *
 * This component is used in both create and edit views.
 */
export function PolicyConfigurationFormSection({
  onChange,
  environmentType,
  policyConfiguration = {
    name: '',
    type: (environmentType === 'kubernetes'
      ? 'security-k8s'
      : 'security-docker') as PolicyType,
    environmentGroupIds: [],
  },
  disableCategory = false,
}: Props) {
  const { name, type, environmentGroupIds } = policyConfiguration;
  const { environments } = useEnvironmentList();
  const groupsOptionsQuery = useGroups({
    select: (environmentGroups) =>
      getFilteredEnvironmentGroupOptions(
        environmentType,
        environments,
        environmentGroups
      ),
  });
  const groupOptions = groupsOptionsQuery.data || [];

  return (
    <FormSection title="Policy configuration">
      <FormControl
        label="Name"
        required
        errors={name ? undefined : 'Name is required'}
      >
        <Input
          type="text"
          value={name}
          onChange={(e) =>
            onChange({
              name: e.target.value,
              type,
              environmentGroupIds,
            })
          }
          placeholder="e.g. my-policy"
          data-cy="policy-name-input"
        />
      </FormControl>

      {!disableCategory && (
        <FormControl label="Policy type">
          <PortainerSelect<PolicyConfigurationValues['type']>
            value={type}
            onChange={(selectedType) => {
              if (selectedType === null) {
                return;
              }
              onChange({
                type: selectedType,
                name,
                environmentGroupIds,
              });
            }}
            options={getTypeOptions(environmentType)}
            data-cy="policy-type-select"
          />
        </FormControl>
      )}
      {type === 'rbac-k8s' && (
        <TextTip color="orange" className="mb-2">
          This policy will only apply to environments using an Edge Agent
          version 2.37.0 or greater. This policy will override the existing
          access rules for the selected environment groups.
        </TextTip>
      )}

      <FormControl
        label="Environment groups"
        tooltip="Select one or more environment groups to apply this policy to"
        required
      >
        <PortainerSelect<number>
          isMulti
          value={environmentGroupIds}
          onChange={(selectedGroupsIds) => {
            onChange({
              environmentGroupIds: selectedGroupsIds,
              name,
              type,
            });
          }}
          options={groupOptions}
          data-cy="policy-groups-select"
          noOptionsMessage={() => 'No environment groups available.'}
        />
      </FormControl>
    </FormSection>
  );
}

// Helper function to filter environment groups based on environment type
// and map them to Option<number> format
function getFilteredEnvironmentGroupOptions(
  environmentType: PolicyEnvironmentType,
  environments: Environment[],
  allGroups: EnvironmentGroup[]
): Option<number>[] {
  const filteredGroups = allGroups.filter((group) => {
    // Exclude "Unassigned" groups
    if (group.Name.toLowerCase().includes('unassigned')) {
      return false;
    }

    const groupEnvironments = environments.filter(
      (env) => env.GroupId === group.Id
    );

    // For Docker/Swarm/Podman policies, only show groups that contain Docker environments
    if (
      environmentType === 'docker' ||
      environmentType === 'swarm' ||
      environmentType === 'podman'
    ) {
      return groupEnvironments.some((env) => isDockerEnvironment(env.Type));
    }

    // For Kubernetes policies, only show groups that contain Kubernetes environments
    if (environmentType === 'kubernetes') {
      return groupEnvironments.some((env) => isKubernetesEnvironment(env.Type));
    }

    // Default: show all groups (except Unassigned)
    return true;
  });

  return filteredGroups.map((group) => ({
    value: group.Id,
    label: group.Name,
  }));
}

function getTypeOptions(
  environmentType: PolicyEnvironmentType
): Option<PolicyType>[] {
  const k8sPolicyTypes: Option<PolicyType>[] = [
    { value: 'rbac-k8s', label: 'RBAC' },
    { value: 'security-k8s', label: 'Security' },
    { value: 'setup-k8s', label: 'Setup' },
    { value: 'registry-k8s', label: 'Registry' },
  ];

  // docker, swarm, podman share docker policy types
  const dockerPolicyTypes: Option<PolicyType>[] = [
    { value: 'rbac-docker', label: 'RBAC' },
    { value: 'security-docker', label: 'Security' },
    { value: 'setup-docker', label: 'Setup' },
    { value: 'registry-docker', label: 'Registry' },
  ];

  if (environmentType === 'kubernetes') {
    return k8sPolicyTypes;
  }
  return dockerPolicyTypes;
}
